import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Bookmark, GripVertical, X } from "lucide-react";
import { Api } from "../lib/api.js";
import { getActiveProfileId } from "../lib/profile.js";
import { PagodaRoofline } from "../components/ornaments/PagodaRoofline.js";
import { FuLion } from "../components/ornaments/FuLion.js";
import { Handprint } from "../components/ornaments/Handprint.js";
import { FiligreeCorner } from "../components/ornaments/FiligreeCorner.js";

export function Forecourt(): JSX.Element {
  const profileId = getActiveProfileId();
  const qc = useQueryClient();
  const pinsQ = useQuery({
    queryKey: ["pins", profileId],
    queryFn: () => Api.library.pins(profileId!),
    enabled: !!profileId,
  });

  const items = pinsQ.data?.items ?? [];
  const [order, setOrder] = useState<string[] | null>(null);
  const draggingId = useRef<string | null>(null);

  const remove = useMutation({
    mutationFn: (itemId: string) => Api.library.unpin(profileId!, itemId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pins", profileId] }),
  });
  const persistOrder = useMutation({
    mutationFn: (ids: string[]) => Api.library.reorderPins(profileId!, ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pins", profileId] }),
  });

  if (!profileId) {
    return (
      <div>
        <h1 className="font-display text-3xl text-ivory">Favorites</h1>
        <div className="ornament-rule" />
        <div className="card p-8 text-parchment">Pick a profile first.</div>
      </div>
    );
  }

  const displayOrder = order ?? items.map((i) => i.id);
  const byId = new Map(items.map((i) => [i.id, i]));
  const sortedItems = displayOrder.map((id) => byId.get(id)).filter((i): i is NonNullable<typeof i> => !!i);

  function onDragStart(id: string): void { draggingId.current = id; }
  function onDragOver(targetId: string, e: React.DragEvent): void {
    e.preventDefault();
    const dragId = draggingId.current;
    if (!dragId || dragId === targetId) return;
    const cur = order ?? items.map((i) => i.id);
    const next = cur.filter((id) => id !== dragId);
    const idx = next.indexOf(targetId);
    if (idx === -1) return;
    next.splice(idx, 0, dragId);
    setOrder(next);
  }
  function onDragEnd(): void {
    draggingId.current = null;
    if (order) persistOrder.mutate(order);
  }

  return (
    <div className="relative -mx-10 -my-10">
      {/* Banner: pagoda roofline + bulbs strung underneath, against deep lacquer */}
      <div className="bg-lacquer-deep relative pt-2">
        <PagodaRoofline className="w-full h-[180px] block" />
        <div className="marquee-bulbs absolute bottom-3 left-10 right-10">
          {Array.from({ length: 32 }).map((_, i) => <span key={i} />)}
        </div>
      </div>

      {/* Engraved page title on lacquer */}
      <div className="bg-lacquer-deep pb-6 px-10 text-center">
        <div className="section-label text-gold">— FAVORITES —</div>
        <h1 className="font-display text-5xl text-goldHi mt-2 tracking-widest" style={{ letterSpacing: "0.16em" }}>
          OF THE STARS
        </h1>
        <div className="hero-underline mt-3 w-72 mx-auto" />
        <p className="text-parchment text-sm mt-3 max-w-[60ch] mx-auto">
          Each square below is something you've pressed into the concrete — a film,
          a show, a channel you wanted to come back to. Drag to rearrange.
        </p>
      </div>

      {/* Forecourt floor — the actual concrete with plaques laid out on it */}
      <div className="bg-concrete px-8 py-10 min-h-[400px] curtain-frame">
        {pinsQ.isLoading ? (
          <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {Array.from({ length: 6 }).map((_, i) => <li key={i} className="aspect-square skeleton" />)}
          </ul>
        ) : items.length === 0 ? (
          <EmptyForecourt />
        ) : (
          <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 max-w-[1280px] mx-auto">
            {sortedItems.map((it, idx) => (
              <ForecourtPlaque
                key={it.id}
                item={it}
                index={idx}
                onDragStart={() => onDragStart(it.id)}
                onDragOver={(e) => onDragOver(it.id, e)}
                onDragEnd={onDragEnd}
                onRemove={() => remove.mutate(it.id)}
              />
            ))}
          </ul>
        )}
        {items.length > 1 && (
          <div className="mt-8 text-engraved text-xs text-center font-display tracking-smallcaps">
            ◆ DRAG TO REARRANGE · POSITION SAVES AUTOMATICALLY ◆
          </div>
        )}
      </div>
    </div>
  );
}

function ForecourtPlaque({
  item, index, onDragStart, onDragOver, onDragEnd, onRemove,
}: {
  item: {
    id: string;
    title: string;
    thumbnail_url: string | null;
    creator_name: string | null;
    category: string | null;
    is_series_pin?: boolean;
    episode_count?: number;
  };
  index: number;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onRemove: () => void;
}): JSX.Element {
  // Series-level pins go straight to the series page (their id is the
  // virtual "series:<name>" form); everything else opens the normal Watch
  // route.
  const linkTo = item.is_series_pin && item.category
    ? `/series/${encodeURIComponent(item.category)}`
    : `/watch/${encodeURIComponent(item.id)}`;
  // Slight random tilt per square — like real concrete plaques pressed at
  // slightly different angles over the years. Stable by item.id so they
  // don't dance every render.
  const seed = hashString(item.id);
  const tilt = ((seed % 7) - 3) * 0.6; // -1.8° to +1.8°
  return (
    <li
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      className="plaque aspect-square cursor-grab active:cursor-grabbing relative group p-3"
      style={{ transform: `rotate(${tilt.toFixed(2)}deg)` }}
    >
      <Link to={linkTo} className="block w-full h-full focus:outline-none">
        {/* Filigree corners */}
        <FiligreeCorner className="absolute top-1 left-1 text-gold/70" />
        <div className="absolute top-1 right-1 text-gold/70" style={{ transform: "scaleX(-1)" }}>
          <FiligreeCorner className="text-gold/70" />
        </div>
        <div className="absolute bottom-1 left-1 text-gold/70" style={{ transform: "scaleY(-1)" }}>
          <FiligreeCorner className="text-gold/70" />
        </div>
        <div className="absolute bottom-1 right-1 text-gold/70" style={{ transform: "scale(-1, -1)" }}>
          <FiligreeCorner className="text-gold/70" />
        </div>

        {/* Order index — like a date carved at the top */}
        <div className="absolute top-2 left-1/2 -translate-x-1/2 font-display text-engraved text-[10px] tracking-widest">
          № {String(index + 1).padStart(3, "0")}
        </div>

        {/* Center poster, or initials if no thumbnail. Inset to look pressed in. */}
        <div
          className="absolute inset-x-6 top-7 bottom-14 rounded overflow-hidden"
          style={{
            boxShadow: "inset 0 2px 6px rgba(0,0,0,0.45), inset 0 -1px 2px rgba(255,240,200,0.18)",
            border: "1px solid rgba(15,6,6,0.35)",
          }}
        >
          {item.thumbnail_url ? (
            <img
              src={item.thumbnail_url}
              alt=""
              loading="lazy"
              referrerPolicy="no-referrer"
              className="w-full h-full object-cover"
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = "hidden"; }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-elev font-display text-3xl text-engraved">
              {item.title.slice(0, 2).toUpperCase()}
            </div>
          )}
          {/* Handprint watermark over the image */}
          <Handprint className="absolute inset-0 w-full h-full opacity-50 mix-blend-multiply pointer-events-none" />
        </div>

        {/* Series-pin badge — small "SERIES · N EPISODES" stamp so the
            viewer knows this favorite drills into a picker, not straight
            into one episode. */}
        {item.is_series_pin && (
          <div className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded bg-vermilion/85 text-goldHi text-[9px] font-display tracking-smallcaps border border-goldHi/50 z-10">
            SERIES{typeof item.episode_count === "number" && item.episode_count > 0 ? ` · ${item.episode_count}` : ""}
          </div>
        )}

        {/* Engraved title at the bottom — like the celebrity signature/name */}
        <div className="absolute bottom-2 left-3 right-3 text-center">
          <div className="font-display text-engraved text-[11px] tracking-smallcaps uppercase truncate" title={item.title}>
            {item.title}
          </div>
          {(item.creator_name ?? (!item.is_series_pin && item.category)) && (
            <div className="font-display text-engraved/70 text-[9px] mt-0.5 truncate opacity-70">
              {item.creator_name ?? item.category}
            </div>
          )}
        </div>
      </Link>

      {/* Remove button — only on hover */}
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(); }}
        className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-ink/85 text-parchment opacity-0 group-hover:opacity-100 transition flex items-center justify-center hover:text-vermilionHi border border-gold/40"
        aria-label="Remove from favorites"
        style={{ transform: "rotate(0deg)" }}
      >
        <X size={14} />
      </button>
      <GripVertical
        size={12}
        className="absolute bottom-2 right-2 text-engraved/40 opacity-0 group-hover:opacity-100 transition"
      />
    </li>
  );
}

function EmptyForecourt(): JSX.Element {
  return (
    <div className="max-w-[800px] mx-auto py-16 text-center relative">
      <div className="flex justify-center items-end gap-12 mb-8">
        <FuLion className="w-32 h-40" facing="right" />
        <FuLion className="w-32 h-40" facing="left" />
      </div>
      <div className="font-display text-engraved text-2xl tracking-widest" style={{ letterSpacing: "0.18em" }}>
        AN EMPTY COURTYARD
      </div>
      <p className="text-engraved/80 mt-4 max-w-[50ch] mx-auto text-sm">
        Hover any card across the library or home and tap the gold bookmark
        in the corner. Each one you press becomes a square on this floor.
      </p>
      <Link to="/library" className="pill mt-8 inline-flex">
        <Bookmark size={14} /> Browse the library
      </Link>
    </div>
  );
}

/** Stable hash for deterministic per-item tilt. */
function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}
